// Intake logs — operator dashboard over the process-intake-form Lambdas'
// CloudWatch logs. Shows volume/outcome stats, a per-client breakdown, and a
// searchable invocation list; clicking a row fetches the RAW CloudWatch lines
// on demand through the audited getIntakeLogEvents callable (raw lines carry
// form PHI and are never stored app-side).
//
// Self-gates on logsRole (viewer/admin) — a separate axis from the operator
// gate, so which operators can see client log data is a per-user grant.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

import { getCurrentUser, readUser } from '../firebase';
import {
  applyClientFilters,
  fetchRawLogEvents,
  listSyncState,
  loadInvocations,
  loadLogGroupsConfig,
} from '../intake-logs/firebase';
import {
  IntakeLogInvocation,
  InvocationStatus,
  LogFilters,
  LogsRole,
  RawLogEvent,
  resolveLogsRole,
} from '../intake-logs/types';
import { fmtDuration, fmtWhen, statusLabel } from '../intake-logs/constants';

import '../pm/theme.css';

type Access = { kind: 'loading' } | { kind: 'denied' } | { kind: 'ok'; role: LogsRole };

type RangeKey = '24h' | '7d' | '14d';
const RANGE_MS: Record<RangeKey, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '14d': 14 * 24 * 60 * 60 * 1000,
};

type RawModal =
  | { kind: 'closed' }
  | { kind: 'loading'; inv: IntakeLogInvocation }
  | { kind: 'error'; inv: IntakeLogInvocation; message: string }
  | { kind: 'ok'; inv: IntakeLogInvocation; events: RawLogEvent[]; truncated: boolean };

function statusColor(s: string | null): string {
  if (s === 'success') return 'var(--rr-green, #2e7d32)';
  if (s === 'fail') return 'var(--rr-red, #c62828)';
  if (s === 'skipped') return 'var(--rr-amber, #b26a00)';
  return '#888';
}

const Logs: React.FC = () => {
  const history = useHistory();
  const [access, setAccess] = useState<Access>({ kind: 'loading' });

  const [range, setRange] = useState<RangeKey>('7d');
  const [center, setCenter] = useState('');
  const [lambda, setLambda] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState<IntakeLogInvocation[]>([]);
  const [capped, setCapped] = useState(false);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | undefined>();
  const [serverField, setServerField] = useState<'center' | 'lambda' | 'status' | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  const [lambdaLabels, setLambdaLabels] = useState<Record<string, string>>({});
  const [lastSync, setLastSync] = useState<number>(0);
  const [syncErrors, setSyncErrors] = useState<number>(0);

  const [raw, setRaw] = useState<RawModal>({ kind: 'closed' });

  // ---- access gate ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u: any = await getCurrentUser();
        if (!u) return !cancelled && setAccess({ kind: 'denied' });
        const data = await readUser(u.uid);
        const r = resolveLogsRole(data && data.restricted);
        if (!cancelled) setAccess(r ? { kind: 'ok', role: r } : { kind: 'denied' });
      } catch {
        if (!cancelled) setAccess({ kind: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Server-pushed filters. Search stays client-side.
  const filters: LogFilters = useMemo(
    () => ({
      center: center || undefined,
      lambda: lambda || undefined,
      status: (status || undefined) as InvocationStatus | undefined,
      search,
      date_from: Date.now() - RANGE_MS[range],
    }),
    [center, lambda, status, search, range],
  );

  const reload = useCallback(async () => {
    setBusy(true);
    setLoadErr('');
    try {
      const res = await loadInvocations({ filters });
      setRows(res.items);
      setCapped(res.capped);
      setCursor(res.cursor);
      setServerField(res.serverField);
    } catch (e: any) {
      setLoadErr(e?.message || 'Failed to load logs.');
    } finally {
      setBusy(false);
    }
    // Sync health is best-effort chrome — never block the table on it.
    listSyncState()
      .then((states) => {
        setLastSync(states.reduce((m, s) => Math.max(m, s.last_run_at || 0), 0));
        setSyncErrors(states.filter((s) => s.last_error).length);
      })
      .catch(() => {});
  }, [filters]);

  useEffect(() => {
    if (access.kind !== 'ok') return;
    reload();
  }, [access.kind, range, center, lambda, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (access.kind !== 'ok') return;
    loadLogGroupsConfig()
      .then(({ groups }) => {
        const labels: Record<string, string> = {};
        groups.forEach((g) => (labels[g.key] = g.label));
        setLambdaLabels(labels);
      })
      .catch(() => {});
  }, [access.kind]);

  async function loadOlder() {
    if (!cursor) return;
    setBusy(true);
    try {
      const res = await loadInvocations({ filters, cursor });
      setRows((prev) => [...prev, ...res.items]);
      setCapped(res.capped);
      setCursor(res.cursor);
    } catch (e: any) {
      setLoadErr(e?.message || 'Failed to load more.');
    } finally {
      setBusy(false);
    }
  }

  async function openRaw(inv: IntakeLogInvocation) {
    setRaw({ kind: 'loading', inv });
    try {
      const res = await fetchRawLogEvents(inv.id);
      setRaw({ kind: 'ok', inv, events: res.events, truncated: res.truncated });
    } catch (e: any) {
      setRaw({ kind: 'error', inv, message: e?.message || 'Failed to fetch raw log.' });
    }
  }

  // Everything below the server push is filtered in memory as the user types.
  const filtered = useMemo(
    () => applyClientFilters(rows, filters, serverField),
    [rows, filters, serverField],
  );

  const stats = useMemo(() => {
    let success = 0;
    let fail = 0;
    let skipped = 0;
    for (const r of filtered) {
      if (r.status === 'success') success++;
      else if (r.status === 'fail') fail++;
      else if (r.status === 'skipped') skipped++;
    }
    const decided = success + fail;
    return {
      total: filtered.length,
      success,
      fail,
      skipped,
      rate: decided > 0 ? Math.round((success / decided) * 100) : null,
    };
  }, [filtered]);

  // Per-client table: same filters EXCEPT center, so it stays a breakdown
  // (clicking a row applies that center as the filter).
  const perCenter = useMemo(() => {
    const base = applyClientFilters(rows, { ...filters, center: undefined }, serverField === 'center' ? null : serverField);
    const map = new Map<string, { center: string; total: number; success: number; fail: number; last: number }>();
    for (const r of base) {
      const key = r.center || '(unattributed)';
      let e = map.get(key);
      if (!e) {
        e = { center: key, total: 0, success: 0, fail: 0, last: 0 };
        map.set(key, e);
      }
      e.total++;
      if (r.status === 'success') e.success++;
      if (r.status === 'fail') e.fail++;
      e.last = Math.max(e.last, r.started_at);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [rows, filters, serverField]);

  const centers = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.center && s.add(r.center));
    if (center) s.add(center);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows, center]);

  const lambdas = useMemo(() => {
    const s = new Set<string>(Object.keys(lambdaLabels));
    rows.forEach((r) => r.lambda && s.add(r.lambda));
    return Array.from(s).sort();
  }, [rows, lambdaLabels]);

  if (access.kind === 'loading') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ padding: '40px 28px' }}>
              <IonSpinner />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }
  if (access.kind === 'denied') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ maxWidth: 640, padding: '40px 28px' }}>
              <div className="pm-header">
                <div className="pm-header-titles">
                  <h1>Intake logs</h1>
                  <p>You don’t have logs access. Ask a logs admin to grant you a role.</p>
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const card: React.CSSProperties = {
    background: 'var(--rr-paper, #fff)',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 'var(--pm-radius-lg, 12px)',
    padding: '16px 18px',
    marginTop: 16,
  };
  const input: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 8,
    fontSize: 14,
    background: 'var(--rr-paper, #fff)',
  };
  const tile: React.CSSProperties = { ...card, marginTop: 0, flex: '1 1 140px', textAlign: 'center' };

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 1240, padding: '24px 28px' }}>
            <div
              className="pm-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
            >
              <div className="pm-header-titles">
                <h1>Intake logs</h1>
                <p>
                  Per-client view of the intake-form lambdas, synced from CloudWatch every 5 minutes.
                  {lastSync ? ` Last sync ${fmtWhen(lastSync)}.` : ' No sync has run yet.'}
                  {syncErrors > 0 && (
                    <span style={{ color: statusColor('fail') }}>
                      {' '}
                      {syncErrors} log group{syncErrors > 1 ? 's' : ''} failing to sync — see Admin.
                    </span>
                  )}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="pm-btn" style={{ padding: '8px 16px' }} onClick={reload} disabled={busy}>
                  {busy ? '…' : 'Refresh'}
                </button>
                {access.role === 'admin' && (
                  <button
                    type="button"
                    className="pm-btn"
                    style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                    onClick={() => history.push('/Operator/Logs/Admin')}
                  >
                    Admin
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select style={input} value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="14d">Last 14 days</option>
              </select>
              <select style={input} value={center} onChange={(e) => setCenter(e.target.value)}>
                <option value="">All clients</option>
                {centers.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select style={input} value={lambda} onChange={(e) => setLambda(e.target.value)}>
                <option value="">All lambdas</option>
                {lambdas.map((l) => (
                  <option key={l} value={l}>
                    {lambdaLabels[l] || l}
                  </option>
                ))}
              </select>
              <select style={input} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All outcomes</option>
                <option value="success">Success</option>
                <option value="fail">Fail</option>
                <option value="skipped">Skipped</option>
              </select>
              <input
                style={{ ...input, flex: '1 1 240px' }}
                placeholder="Search session ID, client, error text…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {loadErr && (
              <div style={{ ...card, borderColor: statusColor('fail'), color: statusColor('fail') }}>{loadErr}</div>
            )}

            {/* Stat tiles */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
              <div style={tile}>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{stats.total}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Invocations</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 26, fontWeight: 700, color: statusColor('success') }}>{stats.success}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Success</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 26, fontWeight: 700, color: statusColor('fail') }}>{stats.fail}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Fail</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 26, fontWeight: 700, color: statusColor('skipped') }}>{stats.skipped}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Skipped (dupes)</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{stats.rate == null ? '—' : `${stats.rate}%`}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Success rate</div>
              </div>
            </div>

            {/* Per-client breakdown */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px' }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>By client</h2>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={thTd}>Client</th>
                      <th style={thTd}>Invocations</th>
                      <th style={thTd}>Success</th>
                      <th style={thTd}>Fail</th>
                      <th style={thTd}>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perCenter.map((c) => (
                      <tr
                        key={c.center}
                        style={{
                          borderTop: '1px solid var(--pm-border, #eee)',
                          cursor: 'pointer',
                          background: center === c.center ? 'var(--rr-stone-100, #f4f2ee)' : undefined,
                        }}
                        onClick={() => setCenter(center === c.center ? '' : c.center === '(unattributed)' ? '' : c.center)}
                        title="Click to filter the list to this client"
                      >
                        <td style={thTd}>{c.center}</td>
                        <td style={thTd}>{c.total}</td>
                        <td style={{ ...thTd, color: statusColor('success') }}>{c.success}</td>
                        <td style={{ ...thTd, color: c.fail ? statusColor('fail') : undefined }}>{c.fail}</td>
                        <td style={thTd}>{fmtWhen(c.last)}</td>
                      </tr>
                    ))}
                    {perCenter.length === 0 && !busy && (
                      <tr>
                        <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                          No invocations in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Invocation list */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>
                  Invocations {busy ? <IonSpinner style={{ width: 14, height: 14 }} /> : `(${filtered.length})`}
                </h2>
                <span style={{ fontSize: 12, color: '#888' }}>Click a row to view the raw CloudWatch log (audited).</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={thTd}>When</th>
                      <th style={thTd}>Lambda</th>
                      <th style={thTd}>Client</th>
                      <th style={thTd}>Session</th>
                      <th style={thTd}>Outcome</th>
                      <th style={thTd}>Duration</th>
                      <th style={thTd}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr
                        key={r.id}
                        style={{ borderTop: '1px solid var(--pm-border, #eee)', cursor: 'pointer' }}
                        onClick={() => openRaw(r)}
                      >
                        <td style={thTd}>{fmtWhen(r.started_at)}</td>
                        <td style={thTd}>{lambdaLabels[r.lambda] || r.lambda}</td>
                        <td style={thTd}>{r.center || '—'}</td>
                        <td style={{ ...thTd, fontFamily: 'monospace' }}>{r.session_id || '—'}</td>
                        <td style={{ ...thTd, color: statusColor(r.status), fontWeight: 600 }}>
                          {statusLabel(r.status)}
                          {r.timed_out ? ' (timeout)' : ''}
                        </td>
                        <td style={thTd}>{fmtDuration(r.duration_ms)}</td>
                        <td style={{ ...thTd, whiteSpace: 'normal', maxWidth: 360 }}>
                          {r.error_summary
                            ? r.error_summary.length > 140
                              ? r.error_summary.slice(0, 140) + '…'
                              : r.error_summary
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && !busy && (
                      <tr>
                        <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                          Nothing matches these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {capped && (
                <div style={{ padding: '12px 18px', borderTop: '1px solid var(--pm-border, #eee)' }}>
                  <button type="button" className="pm-btn" style={{ padding: '8px 16px' }} onClick={loadOlder} disabled={busy}>
                    {busy ? '…' : 'Load older'}
                  </button>
                  <span style={{ marginLeft: 10, fontSize: 12, color: '#888' }}>
                    Showing the newest chunk — older rows exist in this window.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Raw-log modal */}
        {raw.kind !== 'closed' && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
            onClick={() => setRaw({ kind: 'closed' })}
          >
            <div
              style={{
                background: 'var(--rr-paper, #fff)',
                borderRadius: 12,
                width: 'min(1000px, 100%)',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 18px',
                  borderBottom: '1px solid var(--pm-border, #eee)',
                }}
              >
                <div>
                  <strong>{raw.inv.center || 'Unattributed'}</strong>
                  <span style={{ color: '#888', marginLeft: 10, fontSize: 13 }}>
                    {fmtWhen(raw.inv.started_at)} · session {raw.inv.session_id || '—'} ·{' '}
                    <span style={{ color: statusColor(raw.inv.status) }}>{statusLabel(raw.inv.status)}</span>
                  </span>
                </div>
                <button type="button" className="pm-btn" style={{ padding: '6px 12px' }} onClick={() => setRaw({ kind: 'closed' })}>
                  Close
                </button>
              </div>
              <div style={{ overflow: 'auto', padding: '12px 18px', fontFamily: 'monospace', fontSize: 12 }}>
                {raw.kind === 'loading' && (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <IonSpinner /> <div style={{ color: '#888', marginTop: 8 }}>Fetching raw log from CloudWatch…</div>
                  </div>
                )}
                {raw.kind === 'error' && <div style={{ color: statusColor('fail') }}>{raw.message}</div>}
                {raw.kind === 'ok' && (
                  <>
                    {raw.truncated && (
                      <div style={{ color: statusColor('skipped'), marginBottom: 8 }}>
                        Output truncated — this invocation logged more lines than the viewer cap.
                      </div>
                    )}
                    {raw.events.map((e, i) => (
                      <div key={i} style={{ whiteSpace: 'pre-wrap', borderBottom: '1px solid var(--rr-stone-100, #f4f2ee)', padding: '2px 0' }}>
                        <span style={{ color: '#999' }}>{new Date(e.ts).toLocaleTimeString()} </span>
                        {e.message}
                      </div>
                    ))}
                    {raw.events.length === 0 && <div style={{ color: '#888' }}>No raw lines returned (CloudWatch may have expired them).</div>}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

const thTd: React.CSSProperties = { padding: '9px 12px', whiteSpace: 'nowrap' };

export default Logs;
